import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { FilePlus2, Layout, Save, ShieldCheck, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import {
    clearCurrentModuleCleContent,
    clearModuleCleContentError,
    clearModuleCleContentMessage,
    createModuleCleContentRequest,
    updateModuleCleContentRequest,
} from "../../../redux/actions/moduleCleContentActions";
import {
    FILE_CONTENT_TYPES,
    getModuleCleContentGroupFromType,
    MODULE_CLE_CONTENT_GROUP_OPTIONS,
    MODULE_CLE_CONTENT_TYPE_OPTIONS_BY_GROUP,
} from "../moduleCleContentHelpers";
import HtmlRichEditor from "./HtmlRichEditor";

const buildEmptyForm = () => ({
    title: "",
    description: "",
    contentGroup: "DOCUMENTS",
    contentType: "PDF",
    sectionId: "",
    ordre: "",
    active: true,
    visible: true,
    requiredContent: false,
    articleContent: "",
    externalUrl: "",
    evaluationId: "",
    quizId: "",
    evenementId: "",
    certificatId: "",
});

const resetSourceFields = (base) => ({
    ...base,
    articleContent: "",
    externalUrl: "",
    evaluationId: "",
    quizId: "",
    evenementId: "",
    certificatId: "",
});

export default function ModuleCleContentFormPanel({
    open,
    mode,
    moduleCleId,
    sections,
    initialSectionId,
    onClose,
    evaluations,
    quizzes,
    events,
    certificats,
}) {
    const dispatch = useDispatch();
    const { currentContent, loading } = useSelector((state) => state.moduleCleContent || {});
    const [form, setForm] = useState(buildEmptyForm());
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        if (!open) return;

        if (mode === "edit" && currentContent) {
            setForm({
                title: currentContent.title || "",
                description: currentContent.description || "",
                contentGroup: getModuleCleContentGroupFromType(currentContent.contentType),
                contentType: currentContent.contentType || "PDF",
                sectionId: currentContent.sectionId ? String(currentContent.sectionId) : "",
                ordre: currentContent.ordre ? String(currentContent.ordre) : "",
                active: currentContent.active !== false,
                visible: currentContent.visible !== false,
                requiredContent: !!currentContent.requiredContent,
                articleContent: currentContent.articleContent || "",
                externalUrl: currentContent.externalUrl || "",
                evaluationId: currentContent.evaluationId ? String(currentContent.evaluationId) : "",
                quizId: currentContent.quizId ? String(currentContent.quizId) : "",
                evenementId: currentContent.evenementId ? String(currentContent.evenementId) : "",
                certificatId: currentContent.certificatId ? String(currentContent.certificatId) : "",
            });
        } else {
            setForm({ ...buildEmptyForm(), sectionId: initialSectionId ? String(initialSectionId) : "" });
        }

        setSelectedFile(null);
    }, [currentContent, initialSectionId, mode, open]);

    const currentTypeOptions = useMemo(
        () => MODULE_CLE_CONTENT_TYPE_OPTIONS_BY_GROUP[form.contentGroup] || [],
        [form.contentGroup]
    );

    const isFileType = FILE_CONTENT_TYPES.includes(form.contentType);

    if (!open) return null;

    const updateField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

    const handleGroupChange = (contentGroup) => {
        const firstType = MODULE_CLE_CONTENT_TYPE_OPTIONS_BY_GROUP[contentGroup]?.[0]?.value || "ARTICLE";
        setForm((previous) => resetSourceFields({ ...previous, contentGroup, contentType: firstType }));
        setSelectedFile(null);
    };

    const handleTypeChange = (contentType) => {
        setForm((previous) => resetSourceFields({ ...previous, contentType }));
        setSelectedFile(null);
    };

    const validate = () => {
        if (!form.title.trim()) return "Le titre du contenu est obligatoire.";
        if (!form.sectionId) return "La section est obligatoire.";
        if (form.ordre && Number(form.ordre) <= 0) return "L'ordre doit être supérieur à 0.";
        if (isFileType && !selectedFile && !(mode === "edit" && currentContent?.fileName)) {
            return "Un fichier est obligatoire pour ce type de contenu.";
        }
        if (form.contentType === "ARTICLE" && !form.articleContent.trim()) return "Le contenu de l'article est obligatoire.";
        if (form.contentType === "LIEN_EXTERNE" && !form.externalUrl.trim()) return "L'URL externe est obligatoire.";
        if (form.contentType === "EVALUATION" && !form.evaluationId) return "Sélectionnez une évaluation.";
        if (form.contentType === "QUIZ" && !form.quizId) return "Sélectionnez un quiz.";
        if (form.contentType === "EVENEMENT" && !form.evenementId) return "Sélectionnez un événement.";
        if (form.contentType === "CERTIFICAT" && !form.certificatId) return "Sélectionnez un certificat.";
        return null;
    };

    const buildPayload = () => ({
        title: form.title.trim(),
        description: form.description.trim() || null,
        contentType: form.contentType,
        sectionId: Number(form.sectionId),
        ordre: form.ordre ? Number(form.ordre) : null,
        active: !!form.active,
        visible: !!form.visible,
        requiredContent: !!form.requiredContent,
        articleContent: form.contentType === "ARTICLE" ? form.articleContent.trim() : null,
        externalUrl: form.contentType === "LIEN_EXTERNE" ? form.externalUrl.trim() : null,
        evaluationId: form.contentType === "EVALUATION" ? Number(form.evaluationId) : null,
        quizId: form.contentType === "QUIZ" ? Number(form.quizId) : null,
        evenementId: form.contentType === "EVENEMENT" ? Number(form.evenementId) : null,
        certificatId: form.contentType === "CERTIFICAT" ? Number(form.certificatId) : null,
        unlockMode: mode === "edit" ? currentContent?.unlockMode || null : null,
        unlockDate: mode === "edit" ? currentContent?.unlockDate || null : null,
        unlockStartDate: mode === "edit" ? currentContent?.unlockStartDate || null : null,
        unlockEndDate: mode === "edit" ? currentContent?.unlockEndDate || null : null,
        prerequisiteContentId: mode === "edit" ? currentContent?.prerequisiteContentId || null : null,
    });

    const handleSubmit = () => {
        const validationError = validate();
        if (validationError) {
            Swal.fire({ icon: "warning", text: validationError, timer: 2200, showConfirmButton: false });
            return;
        }

        const payload = buildPayload();
        const callbacks = {
            onSuccess: () => {
                dispatch(clearModuleCleContentError());
                dispatch(clearModuleCleContentMessage());
                dispatch(clearCurrentModuleCleContent());
                onClose();
            },
        };

        if (mode === "edit" && currentContent?.id) {
            dispatch(updateModuleCleContentRequest(moduleCleId, currentContent.id, payload, selectedFile, callbacks));
        } else {
            dispatch(createModuleCleContentRequest(moduleCleId, payload, selectedFile, callbacks));
        }
    };

    const renderRelationSelect = (field, label, items, labelResolver) => (
        <label className="mcp-field mcp-field--full">
            <span>{label}</span>
            <select value={form[field]} onChange={(event) => updateField(field, event.target.value)}>
                <option value="">- Sélectionner -</option>
                {items.map((item) => (
                    <option key={item.id} value={String(item.id)}>
                        {labelResolver(item)}
                    </option>
                ))}
            </select>
        </label>
    );

    return (
        <aside className={`mcp-panel ${open ? "is-open" : ""}`}>
            <header className="mcp-panel__header">
                <h2>
                    <FilePlus2 size={18} />
                    {mode === "edit" ? "Modifier le contenu" : "Nouveau contenu"}
                </h2>
                <button
                    type="button"
                    className="mcp-icon-btn"
                    onClick={() => {
                        dispatch(clearCurrentModuleCleContent());
                        onClose();
                    }}
                >
                    <X size={20} />
                </button>
            </header>

            <div className="mcp-panel__body">
                <section className="mcp-form-section">
                    <div className="mcp-form-section__title">Informations générales</div>
                    <div className="mcp-field">
                        <label>Titre du contenu *</label>
                        <input
                            value={form.title}
                            onChange={(event) => updateField("title", event.target.value)}
                            placeholder="Ex: Guide d'utilisation PDF"
                        />
                    </div>
                    <div className="mcp-field">
                        <label>Description</label>
                        <textarea
                            rows={2}
                            value={form.description}
                            onChange={(event) => updateField("description", event.target.value)}
                            placeholder="Brève description du contenu..."
                        />
                    </div>
                    <div className="mcp-field-group">
                        <div className="mcp-field">
                            <label>Famille *</label>
                            <select value={form.contentGroup} onChange={(event) => handleGroupChange(event.target.value)}>
                                {MODULE_CLE_CONTENT_GROUP_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mcp-field">
                            <label>Type *</label>
                            <select value={form.contentType} onChange={(event) => handleTypeChange(event.target.value)}>
                                {currentTypeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="mcp-field-group">
                        <div className="mcp-field">
                            <label>Section *</label>
                            <select value={form.sectionId} onChange={(event) => updateField("sectionId", event.target.value)}>
                                <option value="">- Sélectionner -</option>
                                {(sections || []).map((item) => (
                                    <option key={item.id} value={String(item.id)}>
                                        Section {item.ordre} - {item.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mcp-field">
                            <label>Ordre</label>
                            <input
                                type="number"
                                min="1"
                                placeholder="Auto"
                                value={form.ordre}
                                onChange={(event) => updateField("ordre", event.target.value)}
                            />
                        </div>
                    </div>
                </section>

                <section className="mcp-form-section">
                    <div className="mcp-form-section__title">Source du contenu</div>
                    {isFileType && (
                        <div className="mcp-field">
                            <label>Fichier {form.contentType === "SCORM" ? "(ZIP SCORM)" : ""}</label>
                            <div className="mcp-upload-zone" onClick={() => document.getElementById("mcp-file-input").click()}>
                                <input 
                                    id="mcp-file-input"
                                    type="file" 
                                    style={{ display: "none" }}
                                    onChange={(event) => setSelectedFile(event.target.files?.[0] || null)} 
                                />
                                <FilePlus2 size={24} className="mcp-upload-icon" />
                                <div className="mcp-upload-text">
                                    {selectedFile?.name || currentContent?.fileName || "Cliquez pour sélectionner un fichier"}
                                </div>
                                <div className="mcp-upload-subtext">Ou glissez-déposez le fichier ici</div>
                            </div>
                        </div>
                    )}

                    {form.contentType === "ARTICLE" && (
                        <div className="mcp-field">
                            <label>Contenu de l'article</label>
                            <HtmlRichEditor value={form.articleContent} onChange={(html) => updateField("articleContent", html)} />
                        </div>
                    )}

                    {form.contentType === "LIEN_EXTERNE" && (
                        <div className="mcp-field">
                            <label>URL du lien externe</label>
                            <input
                                type="url"
                                placeholder="https://example.com/ma-ressource"
                                value={form.externalUrl}
                                onChange={(event) => updateField("externalUrl", event.target.value)}
                            />
                        </div>
                    )}

                    {form.contentType === "EVALUATION" &&
                        renderRelationSelect("evaluationId", "Sélectionner l'évaluation", evaluations, (item) => item.name || item.nom || item.code)}
                    {form.contentType === "QUIZ" &&
                        renderRelationSelect("quizId", "Sélectionner le quiz", quizzes, (item) => item.titre || item.nom)}
                    {form.contentType === "EVENEMENT" &&
                        renderRelationSelect("evenementId", "Sélectionner l'événement", events, (item) => item.titre || item.nom)}
                    {form.contentType === "CERTIFICAT" &&
                        renderRelationSelect("certificatId", "Sélectionner le certificat", certificats, (item) => item.nom || item.reference)}
                </section>

                <section className="mcp-form-section">
                    <div className="mcp-form-section__title">Paramètres et options</div>
                    <div className="mcp-field">
                        <label>Contenu obligatoire ?</label>
                        <div className="mcp-radio-cards">
                            <div
                                className={`mcp-radio-card ${form.requiredContent ? "is-selected" : ""}`}
                                onClick={() => updateField("requiredContent", true)}
                            >
                                <strong>Oui, obligatoire</strong>
                                <span>Doit être complété pour valider le module</span>
                            </div>
                            <div
                                className={`mcp-radio-card ${!form.requiredContent ? "is-selected" : ""}`}
                                onClick={() => updateField("requiredContent", false)}
                            >
                                <strong>Non, optionnel</strong>
                                <span>Contenu facultatif pour l'apprenant</span>
                            </div>
                        </div>
                    </div>
                    <div className="mcp-field-group">
                        <label className="mcp-toggle">
                            <input
                                type="checkbox"
                                checked={form.visible}
                                onChange={(event) => updateField("visible", event.target.checked)}
                            />
                            <span>Visible par les apprenants</span>
                        </label>
                        <label className="mcp-toggle">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(event) => updateField("active", event.target.checked)}
                            />
                            <span>Contenu actif</span>
                        </label>
                    </div>
                </section>
            </div>

            <footer className="mcp-panel__footer">
                <button
                    type="button"
                    className="mcp-btn mcp-btn--ghost"
                    onClick={() => {
                        dispatch(clearCurrentModuleCleContent());
                        onClose();
                    }}
                >
                    Annuler
                </button>
                <button type="button" className="mcp-btn mcp-btn--primary" onClick={handleSubmit} disabled={loading}>
                    <Save size={16} />
                    {loading ? "Enregistrement..." : "Enregistrer le contenu"}
                </button>
            </footer>
        </aside>
    );
}
